/**
 * Messages with attachments wait in the outbox until their uploads are done (core `Files.attachmentsStatus`), then go
 * out once through the normal send mutation with `fileIds` (clientId = Idempotency-Key, so a replay never posts
 * twice). Waiting messages survive reloads: the outbox entry (persisted with the query cache) keeps its upload ids and
 * the uploads resume from IndexedDB.
 */
import { Files, Outbox, type PendingEntry, type SendInput } from "@bundle/core";
import type { QueryClient } from "@tanstack/query-core";
import { getOutbox, keys, updateOutbox } from "../data/keys.ts";
import type { SendVars } from "../data/mutations.ts";
import type { UploadManager } from "./uploads.ts";

export class SendGate {
  /** clientId → watched workspace. */
  private readonly watching = new Map<string, string>();
  /** clientIds handed to the send mutation (never twice). */
  private readonly released = new Set<string>();
  /** Sent (or discarded) messages' uploads are dropped locally once their outbox entry is gone. */
  private readonly owned = new Map<string, { ws: string; uploadIds: string[] }>();

  constructor(
    private readonly client: QueryClient,
    private readonly uploads: UploadManager,
    private readonly release: (ws: string, input: SendInput, fileIds: string[]) => void,
  ) {
    uploads.subscribe(() => this.check());
    client.getQueryCache().subscribe((e) => {
      if (e.type === "updated" && e.query.queryKey[2] === "outbox") this.check();
    });
  }

  /** A new message with attachments: show it at once, send it when its files are uploaded. */
  hold(ws: string, input: SendInput, meId: string): void {
    updateOutbox(this.client, ws, (o) => Outbox.insertPending(o, input, meId));
    this.watching.set(input.clientId, ws);
    if (input.uploadIds !== undefined) this.owned.set(input.clientId, { ws, uploadIds: input.uploadIds });
    this.check();
  }

  /** After a restore: re-arm waiting entries of `ws` whose send isn't queued already. */
  resume(ws: string): void {
    for (const e of getOutbox(this.client, ws)) {
      const id = e.message.clientId;
      if (id === undefined || e.uploadIds === undefined) continue;
      this.owned.set(id, { ws, uploadIds: e.uploadIds });
      if (e.status !== "sending" || this.hasSendMutation(id)) continue;
      this.watching.set(id, ws);
    }
    this.check();
  }

  /** Waiting entries the gate will deliver (they are not orphans after a restart). */
  isWaiting(entry: PendingEntry): boolean {
    return entry.uploadIds !== undefined && entry.uploadIds.every((u) => this.uploads.has(u));
  }

  /** Retry a failed message with attachments: retry its failed uploads and wait again. */
  retry(ws: string, entry: PendingEntry): void {
    const id = entry.message.clientId;
    if (id === undefined || entry.uploadIds === undefined) return;
    for (const u of entry.uploadIds) this.uploads.retry(u);
    this.released.delete(id);
    updateOutbox(this.client, ws, (o) => Outbox.retryPending(o, id));
    this.watching.set(id, ws);
    this.check();
  }

  private hasSendMutation(clientId: string): boolean {
    return this.client
      .getMutationCache()
      .findAll({ mutationKey: ["send"] })
      .some((m) => (m.state.variables as SendVars | undefined)?.input.clientId === clientId && m.state.status !== "error");
  }

  private check(): void {
    for (const [clientId, o] of this.owned) {
      if (getOutbox(this.client, o.ws).some((e) => e.message.clientId === clientId)) continue;
      this.owned.delete(clientId);
      for (const u of o.uploadIds) this.uploads.remove(u, false);
    }
    if (this.watching.size === 0) return;
    const states = this.uploads.states();
    for (const [clientId, ws] of this.watching) {
      const entry = getOutbox(this.client, ws).find((e) => e.message.clientId === clientId);
      if (entry === undefined || entry.uploadIds === undefined) {
        this.watching.delete(clientId);
        continue;
      }
      const status = Files.attachmentsStatus(entry.uploadIds, states);
      if (status.status === "waiting") continue;
      this.watching.delete(clientId);
      if (status.status === "failed") {
        updateOutbox(this.client, ws, (o) => Outbox.failPending(o, clientId));
        continue;
      }
      if (this.released.has(clientId)) continue;
      this.released.add(clientId);
      this.release(ws, inputOf(entry), status.fileIds);
    }
  }

  /** The persisted outbox key, for callers that observe it. */
  static key(ws: string) {
    return keys.outbox(ws);
  }
}

/** Rebuilds the SendInput of an outbox entry (attachments included). */
export function inputOf(entry: PendingEntry): SendInput {
  const m = entry.message;
  const input: SendInput = { clientId: m.clientId ?? m.id, channelId: m.channelId, text: m.text, createdAt: m.createdAt };
  if (m.threadRootId !== undefined) input.threadRootId = m.threadRootId;
  if (m.alsoSentToChannel === true) input.alsoSendToChannel = true;
  if (entry.uploadIds !== undefined) {
    input.uploadIds = entry.uploadIds;
    input.files = m.files;
  }
  return input;
}
